import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'

export interface AdminSelectOption {
  value: string
  label: string
}

interface AdminSelectProps {
  value: string
  onChange: (value: string) => void
  options: AdminSelectOption[]
  placeholder?: string
  className?: string
  openUpward?: boolean
  size?: 'default' | 'sm'
}

/**
 * Custom dropdown matching the approver-queue mock design (button + menu,
 * bold values, selected row highlighted). Ported from the mock's Select;
 * the mock's `ink` accent is mapped to the app's `gray-900`.
 */
export function AdminSelect({
  value,
  onChange,
  options,
  placeholder,
  className = '',
  openUpward = false,
  size = 'default',
}: AdminSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const selectedOption = options.find((option) => option.value === value)
  const isSmall = size === 'sm'
  const hasCustomHeight = className.includes('h-')
  const hasCustomTextSize = className.includes('text-')
  const defaultHeight = hasCustomHeight ? '' : isSmall ? 'h-9' : 'h-12'
  const defaultTextSize = hasCustomTextSize ? '' : isSmall ? 'text-sm' : 'text-base'

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false)
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setIsOpen((open) => !open)
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!isOpen) setIsOpen(true)
    }
  }

  const handleSelect = (optionValue: string) => {
    onChange(optionValue)
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        onKeyDown={handleKeyDown}
        className={`relative flex w-full items-center ${defaultHeight} ${defaultTextSize} ${isSmall ? 'px-2.5 pr-7' : 'px-4 pr-10'} cursor-pointer rounded-xl border border-gray-300 bg-white text-left font-medium text-gray-900 transition-colors hover:border-gray-400 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary ${className}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-400'}>
          {selectedOption?.label || placeholder || 'Select...'}
        </span>
        <ChevronDown
          className={`absolute ${isSmall ? 'right-2 h-4 w-4' : 'right-4 h-5 w-5'} pointer-events-none top-1/2 -translate-y-1/2 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          className={`absolute z-[60] w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg ${openUpward ? 'bottom-full mb-2' : 'mt-2'}`}
        >
          <div className="max-h-60 overflow-y-auto" role="listbox" aria-label="Options">
            {options.map((option) => {
              const isSelected = option.value === value
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={`flex w-full items-center justify-between ${isSmall ? 'px-2.5 py-2 text-sm' : 'px-4 py-3 text-base'} text-left font-medium transition-colors ${isSelected ? 'bg-brand-primary text-[var(--brand-primary-text)]' : 'bg-white text-gray-900 hover:bg-gray-50'}`}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span>{option.label}</span>
                  {isSelected && <Check className="h-4 w-4" />}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
